import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Mail, Eye, MousePointerClick, AlertTriangle, Send, Search,
  ChevronRight, RefreshCw, Users, Package, ArrowLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diffM = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffM < 1) return 'just now';
  if (diffM < 60) return `${diffM}m ago`;
  const h = Math.floor(diffM / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const EngagementTab = () => {
  const [view, setView] = useState('per-load'); // 'per-load' | 'per-carrier'
  const [loads, setLoads] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [loadDetail, setLoadDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const [carrierEmail, setCarrierEmail] = useState('');
  const [carrierData, setCarrierData] = useState(null);
  const [carrierLoading, setCarrierLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/my-loads`);
        setLoads(res.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openLoadDetail = async (load) => {
    setSelectedLoad(load);
    setLoadDetail(null);
    try {
      const res = await axios.get(`${API}/loads/${load.id}/notifications`);
      setLoadDetail(res.data);
    } catch (e) {
      setLoadDetail({ error: e?.response?.data?.detail || 'Failed to load engagement data' });
    }
  };

  const searchCarrier = async () => {
    const email = carrierEmail.trim().toLowerCase();
    if (!email) return;
    setCarrierLoading(true);
    setCarrierData(null);
    try {
      const res = await axios.get(`${API}/carriers/email-history`, { params: { email } });
      setCarrierData(res.data);
    } catch (e) {
      setCarrierData({ error: e?.response?.data?.detail || 'Failed to fetch history' });
    } finally {
      setCarrierLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="engagement-tab">
      <div className="bg-zinc-900 text-white px-5 py-4">
        <div className="flex items-center gap-3 mb-1">
          <Mail className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-black uppercase tracking-wide">Carrier Engagement</h2>
        </div>
        <p className="text-zinc-400 text-xs leading-relaxed max-w-2xl">
          See who got notified about each load, who opened it, and who clicked through — or look up a specific carrier&apos;s email history.
        </p>
      </div>

      {/* View switcher */}
      <div className="flex border border-zinc-200 bg-white w-fit" data-testid="engagement-view-switch">
        <button
          onClick={() => { setView('per-load'); setSelectedLoad(null); }}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${view === 'per-load' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
          data-testid="view-per-load"
        >
          <Package className="w-3.5 h-3.5 inline mr-1.5" />
          Per Load
        </button>
        <button
          onClick={() => setView('per-carrier')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-l border-zinc-200 ${view === 'per-carrier' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
          data-testid="view-per-carrier"
        >
          <Users className="w-3.5 h-3.5 inline mr-1.5" />
          Per Carrier
        </button>
      </div>

      {view === 'per-load' && !selectedLoad && (
        <LoadList loads={loads} loading={loading} onSelect={openLoadDetail} />
      )}

      {view === 'per-load' && selectedLoad && (
        <LoadDetail
          load={selectedLoad}
          detail={loadDetail}
          onBack={() => { setSelectedLoad(null); setLoadDetail(null); }}
          onRefresh={() => openLoadDetail(selectedLoad)}
        />
      )}

      {view === 'per-carrier' && (
        <PerCarrierLookup
          email={carrierEmail}
          setEmail={setCarrierEmail}
          onSearch={searchCarrier}
          loading={carrierLoading}
          data={carrierData}
        />
      )}
    </div>
  );
};

const LoadList = ({ loads, loading, onSelect }) => {
  if (loading) return <div className="py-16 text-center"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400 mx-auto" /></div>;
  if (!loads.length) return <div className="py-16 text-center text-sm text-zinc-500">No loads to show.</div>;
  return (
    <div className="bg-white border border-zinc-200">
      <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
        <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">
          Pick a load to see its notification trail
        </span>
      </div>
      <div className="divide-y divide-zinc-100">
        {loads.slice(0, 100).map((load) => (
          <button
            key={load.id}
            onClick={() => onSelect(load)}
            className="w-full text-left px-5 py-3 hover:bg-zinc-50 flex items-center justify-between transition-colors"
            data-testid={`engagement-load-row-${load.reference_no}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-mono font-bold text-zinc-500">#{load.reference_no}</span>
                {load.source === 'tms' && (
                  <span className="text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 font-bold uppercase">TMS</span>
                )}
                <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${
                  load.status === 'available' ? 'bg-green-100 text-green-700' :
                  load.status === 'expired' ? 'bg-zinc-200 text-zinc-500 line-through' :
                  'bg-amber-100 text-amber-700'
                }`}>{load.status}</span>
              </div>
              <div className="text-sm font-bold text-zinc-900 truncate">
                {load.pickup} <span className="text-red-600">→</span> {load.destination}
              </div>
              <div className="text-[11px] text-zinc-500">{load.truck_type} · {load.commodity}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0 ml-3" />
          </button>
        ))}
      </div>
    </div>
  );
};

const LoadDetail = ({ load, detail, onBack, onRefresh }) => {
  if (!detail) return <div className="py-16 text-center"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400 mx-auto" /></div>;
  if (detail.error) {
    return (
      <div className="bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-900">
        <button onClick={onBack} className="text-xs font-bold uppercase tracking-wider mb-2 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Back</button>
        <div>{detail.error}</div>
      </div>
    );
  }
  const { summary, recipients } = detail;
  return (
    <div className="space-y-4" data-testid="engagement-load-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-red-600 inline-flex items-center gap-1" data-testid="engagement-back-btn">
          <ArrowLeft className="w-3 h-3" />
          Back to loads
        </button>
        <button onClick={onRefresh} className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-red-600 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" />Refresh
        </button>
      </div>

      <div className="bg-white border border-zinc-200 px-5 py-4">
        <div className="text-[10px] font-mono font-bold text-zinc-500 mb-1">Ref #{load.reference_no}</div>
        <div className="text-lg font-black text-zinc-900">{load.pickup} <span className="text-red-600">→</span> {load.destination}</div>
        <div className="text-xs text-zinc-500">{load.truck_type} · {load.commodity}</div>
      </div>

      {/* Engagement KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={Send}     label="Notified"  value={summary.notified}  color="zinc"  />
        <KPI icon={Mail}     label="Delivered" value={summary.delivered} color="zinc"  />
        <KPI icon={Eye}      label="Opened"    value={summary.opened}    color="green" suffix={`${summary.open_rate}%`}  />
        <KPI icon={MousePointerClick} label="Clicked" value={summary.clicked} color="red" suffix={`${summary.click_rate}%`} />
        <KPI icon={AlertTriangle} label="Bounced" value={summary.bounced} color="amber" />
      </div>

      {/* Recipients table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">
            Recipients ({recipients.length})
          </span>
        </div>
        {recipients.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500">
            No carriers were notified for this load yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="engagement-recipients-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">Carrier</th>
                  <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">Status</th>
                  <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">Opened</th>
                  <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">Clicked</th>
                  <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-zinc-500">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {recipients.map((r) => (
                  <tr key={r.carrier_email} className="hover:bg-zinc-50 text-xs">
                    <td className="px-4 py-2 truncate max-w-[260px] font-mono">{r.carrier_email}</td>
                    <td className="px-3 py-2 text-center">
                      <StatusPill status={r.status} bounced={r.bounced} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.opened ? <span className="text-green-700 font-bold">✓ {r.open_count > 1 ? `×${r.open_count}` : ''}</span> : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.clicked ? <span className="text-red-700 font-bold">✓ {r.click_count > 1 ? `×${r.click_count}` : ''}</span> : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-500 whitespace-nowrap">{formatRelative(r.sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const PerCarrierLookup = ({ email, setEmail, onSearch, loading, data }) => (
  <div className="space-y-4" data-testid="engagement-per-carrier">
    <div className="bg-white border border-zinc-200 px-5 py-4">
      <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">Carrier Email</label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="driver@example.com"
            className="w-full bg-zinc-50 border border-zinc-200 text-sm pl-9 pr-3 py-2 focus:outline-none focus:border-red-600"
            data-testid="engagement-carrier-input"
          />
        </div>
        <button
          onClick={onSearch}
          disabled={loading || !email.trim()}
          className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-300 text-white font-bold uppercase tracking-wider text-xs px-4"
          data-testid="engagement-carrier-search"
        >
          {loading ? '...' : 'Look Up'}
        </button>
      </div>
    </div>

    {data?.error && (
      <div className="bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-900">{data.error}</div>
    )}

    {data && !data.error && (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI icon={Mail} label="Emails Received" value={data.summary.total} color="zinc" />
          <KPI icon={Eye}  label="Opened" value={data.summary.opened} color="green" />
          <KPI icon={MousePointerClick} label="Clicked" value={data.summary.clicked} color="red" />
          <KPI icon={AlertTriangle} label="Bounced" value={data.summary.bounced} color="amber" />
        </div>

        <div className="bg-white border border-zinc-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">Email History ({data.emails.length})</span>
          </div>
          {data.emails.length === 0 ? (
            <div className="py-12 text-center text-sm text-zinc-500">No emails sent to this carrier yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">Subject / Load</th>
                    <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">Status</th>
                    <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">Opened</th>
                    <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">Clicked</th>
                    <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-zinc-500">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-xs">
                  {data.emails.map((e, i) => (
                    <tr key={e.id || i} className="hover:bg-zinc-50">
                      <td className="px-4 py-2">
                        <div className="font-bold text-zinc-900 truncate max-w-[320px]">{e.subject || '(no subject)'}</div>
                        {e.lane && <div className="text-[11px] text-zinc-500">#{e.load_ref} · {e.lane}</div>}
                      </td>
                      <td className="px-3 py-2 text-center"><StatusPill status={e.status} bounced={e.bounced} /></td>
                      <td className="px-3 py-2 text-center">{e.opened ? <span className="text-green-700 font-bold">✓</span> : <span className="text-zinc-300">—</span>}</td>
                      <td className="px-3 py-2 text-center">{e.clicked ? <span className="text-red-700 font-bold">✓</span> : <span className="text-zinc-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-zinc-500 whitespace-nowrap">{formatRelative(e.sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    )}
  </div>
);

const KPI = ({ icon: Icon, label, value, color, suffix }) => {
  const palette = {
    zinc:  'border-zinc-200 text-zinc-900',
    green: 'border-green-200 text-green-700',
    red:   'border-red-200 text-red-700',
    amber: 'border-amber-200 text-amber-700',
  };
  return (
    <div className={`bg-white border ${palette[color] || palette.zinc} px-4 py-3`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" />
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-black tabular-nums">{(value || 0).toLocaleString()}</span>
        {suffix && <span className="text-[11px] font-bold opacity-75">{suffix}</span>}
      </div>
    </div>
  );
};

const StatusPill = ({ status, bounced }) => {
  if (bounced) return <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 font-bold uppercase">Bounced</span>;
  if (status === 'sent') return <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 font-bold uppercase">Sent</span>;
  if (status === 'failed') return <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 font-bold uppercase">Failed</span>;
  return <span className="text-[10px] bg-zinc-100 text-zinc-700 px-1.5 py-0.5 font-bold uppercase">{status}</span>;
};
